<template>
  <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:items-start lg:gap-7">
    <section class="grid gap-5">
      <div class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Basic settings</h3>
        <bly-native-text-field
          :value="model.name"
          label="Name"
          :error="errors.name"
          required
          @input="updateRoot('name', $event)"
        />
        <bly-native-textarea-field
          :value="model.description"
          label="Description"
          @input="updateRoot('description', $event)"
        />
        <bly-native-text-field
          v-if="mode === 'create'"
          :value="model.id"
          label="ID"
          placeholder="Optional custom ID"
          @input="updateRoot('id', $event)"
        />
        <bly-native-select-field
          :value="model.inputProfile.inputProfileProviderId || ''"
          label="Provider"
          :options="providerOptions"
          @input="updateProfile('inputProfileProviderId', $event || null)"
        />
      </div>

      <div class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Claims</h3>
        <div class="grid gap-1">
          <span class="text-xs font-semibold text-[#dde6f3]">Access claims *</span>
          <el-cascader
            :value="model.claimsCanAccess"
            :options="claimsTree"
            :props="{ multiple: true, checkStrictly: true }"
            :show-all-levels="false"
            collapse-tags
            clearable
            class="w-full"
            @input="updateRoot('claimsCanAccess', $event)"
          />
          <span v-if="errors.claimsCanAccess" class="text-xs text-red-300">
            {{ errors.claimsCanAccess }}
          </span>
        </div>

        <div class="mt-3.5 grid gap-1">
          <span class="text-xs font-semibold text-[#dde6f3]">Edit claims *</span>
          <el-cascader
            :value="model.claimsCanEdit"
            :options="claimsTree"
            :props="{ multiple: true, checkStrictly: true }"
            :show-all-levels="false"
            collapse-tags
            clearable
            class="w-full"
            @input="updateRoot('claimsCanEdit', $event)"
          />
          <span v-if="errors.claimsCanEdit" class="text-xs text-red-300">
            {{ errors.claimsCanEdit }}
          </span>
        </div>
      </div>
    </section>

    <section class="grid gap-5">
      <div class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Profile inputs</h3>
        <bly-native-select-field
          :value="model.inputProfile.inputIds"
          label="Inputs"
          :options="inputOptions"
          multiple
          @input="updateProfile('inputIds', $event)"
        />
        <bly-native-select-field
          :value="model.inputProfile.supportedTypes"
          label="Supported types"
          :options="supportedTypeOptions"
          multiple
          @input="updateProfile('supportedTypes', $event)"
        />
        <bly-native-select-field
          :value="model.inputProfile.location || ''"
          label="Location"
          :options="locationOptions"
          @input="updateProfile('location', $event || null)"
        />
      </div>

      <div class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Config</h3>
        <bly-input-profile-config-editor
          :value="model.inputProfile.config"
          :routing-scopes="routingScopes"
          @input="updateProfile('config', $event)"
        />
        <span v-if="errors['inputProfile.config']" class="text-xs text-[#fca5a5]">
          {{ errors['inputProfile.config'] }}
        </span>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "InputProfilePermissionForm",
  props: {
    value: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    mode: {
      type: String,
      default: "create",
    },
    claimsTree: {
      type: Array,
      default: () => [],
    },
    inputs: {
      type: Array,
      default: () => [],
    },
    providers: {
      type: Array,
      default: () => [],
    },
    inputTypes: {
      type: Array,
      default: () => [],
    },
    locations: {
      type: Array,
      default: () => [],
    },
    routingScopes: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    model() {
      return this.value;
    },
    inputOptions() {
      return this.inputs.map((input) => ({
        value: input.id,
        label: input.type ? `${input.name} (${input.type})` : input.name,
      }));
    },
    providerOptions() {
      return [
        { value: "", label: "— none —" },
        ...this.providers.map((provider) => ({ value: provider.id, label: provider.name })),
      ];
    },
    supportedTypeOptions() {
      return this.inputTypes.map((type) => ({ value: type, label: type }));
    },
    locationOptions() {
      const current = this.model.inputProfile.location;
      const options = this.locations.map((location) => ({
        value: location.code,
        label: `${location.name} (${location.code})`,
      }));
      // keep a value that is no longer in the locations list selectable
      const isKnown = options.some((option) => option.value === current);
      if (current && !isKnown) options.unshift({ value: current, label: current });
      return [{ value: "", label: "— none —" }, ...options];
    },
  },
  methods: {
    updateRoot(field, value) {
      this.$emit("input", {
        ...this.model,
        [field]: value,
      });
    },
    updateProfile(field, value) {
      this.$emit("input", {
        ...this.model,
        inputProfile: {
          ...this.model.inputProfile,
          [field]: value,
        },
      });
    },
  },
};
</script>
